import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, Trophy, Target, TrendingUp, BarChart3, 
  Phone, MapPin, CheckCircle2, DollarSign, Star,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PerformancePage = () => {
  const { user, hasRole } = useAuth();
  const isBuilder = hasRole(['builder_admin', 'super_admin']);
  const isPartner = hasRole(['partner']);
  
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState(null);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    loadPerformanceData();
  }, [periodFilter]);

  const loadPerformanceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/analytics/team-performance?period=${periodFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeamData(data);
      }
    } catch (error) {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString()}`;
  };

  const getRoleBadge = (role) => {
    const styles = {
      'partner': 'bg-amber-100 text-amber-700',
      'sales_team': 'bg-blue-100 text-blue-700',
      'marketing_team': 'bg-violet-100 text-violet-700',
      'sub_user': 'bg-gray-100 text-gray-700',
    };
    const labels = {
      'partner': 'Channel Partner',
      'sales_team': 'Sales Team',
      'marketing_team': 'Marketing',
      'sub_user': 'Team Member',
    };
    return <Badge className={styles[role] || styles.sub_user}>{labels[role] || role}</Badge>;
  };

  const getTierBadge = (tier) => {
    const styles = {
      'bronze': 'bg-amber-700 text-white',
      'silver': 'bg-gray-400 text-white',
      'gold': 'bg-amber-500 text-white',
      'platinum': 'bg-slate-700 text-white',
    };
    return tier ? <Badge className={styles[tier] || styles.bronze}>{tier}</Badge> : null;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Trophy className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Trophy className="h-5 w-5 text-amber-700" />;
    return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  const getConversionTrend = (rate) => {
    if (rate >= 20) return { icon: ArrowUpRight, color: 'text-green-600', label: 'Excellent' };
    if (rate >= 10) return { icon: Minus, color: 'text-amber-600', label: 'Good' };
    return { icon: ArrowDownRight, color: 'text-red-600', label: 'Needs Improvement' };
  };

  const filteredMembers = teamData?.team_members?.filter(member => {
    if (roleFilter === 'all') return true;
    return member.role === roleFilter;
  }) || [];

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-amber-600" />
            {isBuilder ? 'Team Performance' : 'My Team Performance'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isBuilder 
              ? 'Track performance of your employees and channel partners'
              : 'Track performance of your team members'}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="monthly">This Month</SelectItem>
              <SelectItem value="quarterly">This Quarter</SelectItem>
              <SelectItem value="yearly">This Year</SelectItem>
            </SelectContent>
          </Select>
          {isBuilder && (
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="partner">Channel Partners</SelectItem>
                <SelectItem value="sales_team">Sales Team</SelectItem>
                <SelectItem value="marketing_team">Marketing</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teamData?.total_team || 0}</p>
                <p className="text-sm text-muted-foreground">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Target className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teamData?.total_leads || 0}</p>
                <p className="text-sm text-muted-foreground">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teamData?.total_bookings || 0}</p>
                <p className="text-sm text-muted-foreground">Total Bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {teamData?.total_leads > 0 
                    ? Math.round((teamData?.total_bookings / teamData?.total_leads) * 100)
                    : 0}%
                </p>
                <p className="text-sm text-muted-foreground">Avg Conversion</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Performance Leaderboard
          </CardTitle>
          <CardDescription>
            Ranked by bookings ({filteredMembers.length} members)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No team members found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMembers.map((member, index) => {
                const trend = getConversionTrend(member.conversion_rate);
                const TrendIcon = trend.icon;
                return (
                  <div 
                    key={member.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      index === 0 ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' :
                      index === 1 ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200' :
                      index === 2 ? 'bg-gradient-to-r from-amber-50/50 to-amber-100/50 border-amber-200/50' :
                      'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center">
                        {getRankIcon(member.rank)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{member.name}</p>
                          {getRoleBadge(member.role)}
                          {member.tier && getTierBadge(member.tier)}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Target className="h-3 w-3" />
                            {member.leads_count} leads
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            {member.bookings_count} bookings
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <TrendIcon className={`h-4 w-4 ${trend.color}`} />
                        <span className={`text-xl font-bold ${trend.color}`}>
                          {member.conversion_rate}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{trend.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Breakdown Tabs */}
      {isBuilder && (
        <Tabs defaultValue="partners" className="space-y-4">
          <TabsList>
            <TabsTrigger value="partners">Channel Partners</TabsTrigger>
            <TabsTrigger value="employees">Employees</TabsTrigger>
          </TabsList>
          
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Channel Partner Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Partner</th>
                        <th className="pb-3 font-medium text-center">Tier</th>
                        <th className="pb-3 font-medium text-right">Leads</th>
                        <th className="pb-3 font-medium text-right">Bookings</th>
                        <th className="pb-3 font-medium text-right">Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.filter(m => m.role === 'partner').map((member) => (
                        <tr key={member.id} className="border-b hover:bg-muted/50">
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              {member.rank <= 3 && getRankIcon(member.rank)}
                              <span className="font-medium">{member.name}</span>
                            </div>
                          </td>
                          <td className="py-3 text-center">{getTierBadge(member.tier)}</td>
                          <td className="py-3 text-right">{member.leads_count}</td>
                          <td className="py-3 text-right font-medium text-green-600">{member.bookings_count}</td>
                          <td className="py-3 text-right">
                            <span className={getConversionTrend(member.conversion_rate).color}>
                              {member.conversion_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="employees">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Employee Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Employee</th>
                        <th className="pb-3 font-medium text-center">Role</th>
                        <th className="pb-3 font-medium text-right">Leads</th>
                        <th className="pb-3 font-medium text-right">Bookings</th>
                        <th className="pb-3 font-medium text-right">Conversion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.filter(m => ['sales_team', 'marketing_team'].includes(m.role)).map((member) => (
                        <tr key={member.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 font-medium">{member.name}</td>
                          <td className="py-3 text-center">{getRoleBadge(member.role)}</td>
                          <td className="py-3 text-right">{member.leads_count}</td>
                          <td className="py-3 text-right font-medium text-green-600">{member.bookings_count}</td>
                          <td className="py-3 text-right">
                            <span className={getConversionTrend(member.conversion_rate).color}>
                              {member.conversion_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default PerformancePage;
